import React, {
  createContext,
  useContext,
  useMemo,
  type ReactNode,
} from 'react';
import type { ApolloError, ApolloQueryResult } from '@apollo/client';
import type { ConsolidatedEntityTriageReport } from '@investigator/shared/generated/graphql';
import { useAgenticTriageSettings } from '../../hooks';
import { useEntitiesContext } from '../EntitiesContext';
import {
  useGetConsolidatedEntityTriageReport,
  IGetConsolidatedEntityTriageReports,
} from '../../queries';

export interface EntityAgenticTriageReportContextType {
  report: ConsolidatedEntityTriageReport | null;
  isLoading: boolean;
  error: ApolloError | undefined;
  refetch: () => Promise<
    ApolloQueryResult<IGetConsolidatedEntityTriageReports>
  >;
}

const EntityAgenticTriageReportContextInstance = createContext<
  EntityAgenticTriageReportContextType | undefined
>(undefined);

export interface EntityAgenticTriageReportProviderProps {
  children: ReactNode;
}

export const EntityAgenticTriageReportProvider: React.FC<
  EntityAgenticTriageReportProviderProps
> = ({ children }) => {
  const { agenticTriageEnabled } = useAgenticTriageSettings();
  const { selectedEntity } = useEntitiesContext();

  // Use normalized dates from selectedEntity (context handles fallback to global dates)
  const selectedEntityStart = selectedEntity?.start ?? 0;
  const selectedEntityEnd = selectedEntity?.end ?? 0;

  // Determine whether to fetch by report_id or entity_id
  const hasReportId = !!selectedEntity?.agenticTriageReportId;
  const hasTenant = !!selectedEntity?.tenantInfo?.tenant_id;

  // Build variables based on available identifiers
  const variables = useMemo(() => {
    if (hasReportId && hasTenant) {
      // Fetch by report_id when available (more specific, faster)
      return {
        tenantReportIdList: [
          {
            tenant: selectedEntity?.tenantInfo?.tenant_id || '',
            report_id: selectedEntity?.agenticTriageReportId || '',
          },
        ],
      };
    }

    // Fall back to fetching by entity_id + date range
    return {
      tenantEntityInput: {
        entity_id: selectedEntity?.entityId || '',
        tenant: selectedEntity?.tenantInfo?.tenant_id || '',
        start: selectedEntityStart || 0,
        end: selectedEntityEnd || 0,
      },
    };
  }, [
    hasReportId,
    hasTenant,
    selectedEntity?.agenticTriageReportId,
    selectedEntity?.entityId,
    selectedEntity?.tenantInfo?.tenant_id,
    selectedEntityStart,
    selectedEntityEnd,
  ]);

  // Determine skip condition based on fetch mode
  const shouldSkip = useMemo(() => {
    if (!agenticTriageEnabled || !selectedEntity || !hasTenant) {
      return true;
    }

    // If using report_id, we only need report_id and tenant
    if (hasReportId) {
      return !selectedEntity.agenticTriageReportId;
    }

    // If using entity_id, we need entity_id, tenant, start, and end
    return (
      !selectedEntity.entityId || !selectedEntityStart || !selectedEntityEnd
    );
  }, [
    agenticTriageEnabled,
    selectedEntity,
    hasTenant,
    hasReportId,
    selectedEntityStart,
    selectedEntityEnd,
  ]);

  const { data, loading, error, refetch } =
    useGetConsolidatedEntityTriageReport({
      variables,
      skip: shouldSkip,
    });

  const value = useMemo<EntityAgenticTriageReportContextType>(
    () => ({
      // Backend returns array, take first item
      report: data?.getConsolidatedEntityTriageReports?.[0] || null,
      isLoading: loading,
      error,
      refetch,
    }),
    [data?.getConsolidatedEntityTriageReports, loading, error, refetch],
  );

  // Short-circuit if feature is disabled to avoid expensive provider mounting
  if (!agenticTriageEnabled) {
    return children;
  }

  return (
    <EntityAgenticTriageReportContextInstance.Provider value={value}>
      {children}
    </EntityAgenticTriageReportContextInstance.Provider>
  );
};

export const useEntityAgenticTriageReportContext = () => {
  const context = useContext(EntityAgenticTriageReportContextInstance);
  if (!context) {
    // Return default values when feature is disabled (provider short-circuited)
    return {
      report: null,
      isLoading: false,
      error: undefined,
      refetch: async () =>
        ({}) as ApolloQueryResult<IGetConsolidatedEntityTriageReports>,
    };
  }
  return context;
};
